/**
 * A query-builder "block" wires together the following query components:
 * - header (e.g. "USERS THAT <DID>")
 * - search-bar input (e.g. "Select event")
 * - dropdown (e.g. event selector, time picker)
 */

import findIndex from 'lodash/findIndex';
import findKey from 'lodash/findKey';
import pick from 'lodash/pick';
import userDetailsClient from '../../report/queries/app-api/user-details-client';
import {filterAttributes, serializeAttributes} from '../../util';
import {replaceByIndex} from '../../util/array';
import {emitTrackEvent, propagateEvent, scrollIntoViewIfNeeded} from '../../util/dom';
import {registerMPElement} from 'common/util/register-element';
import {Component} from 'panel';

import css from './stylesheets/index.styl?inline';
import template from './index.jade';

import {ChildMenu} from './child-menu';
import {getCustomEventMenuItem} from './util';
import {THEME_ENUM as ButtonThemes} from '../../components/button';
import {THEME, SELECTOR_TYPE} from './constants';

import BreakdownMenu from './breakdown-menu';
import CohortsMenu from './cohorts-menu';
import EventsMenu from './events-menu';
import FilterMenu from './filter-menu';
import MultiEventsMenu from './multi-events-menu';
import PropertiesMenu from './properties-menu';
import TimeMenu from './time-menu';
import SegmentMenu from './segment-menu';

// Static definition of all registered child menus
export const MENU_TYPES = {
  breakdown: BreakdownMenu,
  cohorts: CohortsMenu,
  events: EventsMenu,
  filter: FilterMenu,
  properties: PropertiesMenu,
  segment: SegmentMenu,
  tabbed: ChildMenu, // TODO: deprecate this
  time: TimeMenu,
  'multi-events': MultiEventsMenu,
};

enum MODAL_TYPE {
  CustomEvent,
}

const COMPONENT_NAME = `mp-query-block`;

interface QueryBlockState {
  isCreatingNewCustomEvent: boolean;
  isHeaderMenuOpen: boolean;
  isMenuOpen: boolean;
  menuType: string;
  permissions: Set<string>;
  searchFilter: string;
  renderCustomEventModal: boolean;
  openModal: MODAL_TYPE | null;
  projectID: number;
  /** @todo: [shawn] Add types for these */
  eventData: any;
  selected: any;
  selectedCustomEventId: string;
  staged: any; // TODO: being used in breakdown-menu, this should be removed
  tooltipData: any;
  keydownProxyEl: any;
}

class QueryBlock extends Component<QueryBlockState> {
  private childMenu: ChildMenu<any>;
  private inputElement: any;

  static get attrsSchema() {
    return {
      ...super.attrsSchema,
      type: {type: `string`, enum: Object.keys(MENU_TYPES)},
      'all-data-groups': `boolean`,
      'allow-cohort-filter': `boolean`,
      'allow-typecast': `boolean`,
      'data-group-id': `string`,
      disabled: `boolean`,
      'display-context': {type: `string`, default: null},
      'exclude-autotrack-events': {
        type: `boolean`,
        default: false,
        description: `whether to hide autotrack events`,
      },
      'enable-behavioral-properties': {type: `boolean`, default: false},
      'exclude-close-selectors': {type: `json`, default: []},
      'exclude-custom-events': `boolean`,
      'include-custom-properties': {
        type: `string`,
        enum: [`none`, `persisted-only`, `all`],
        default: `none`,
      },
      'excluded-elements': {type: `json`, default: []},
      'event-names': {type: `json`, default: null},
      'header-segments': `json`,
      'header-text': `string`,
      'help-text': `string`,
      'input-only': {
        type: `boolean`,
        description: `Set to ignore the selected value from rendering in the block label`,
      },
      'is-blocked-people': `boolean`,
      label: `string`,
      'max-data-history-days': {type: `number`, description: `max days time menu can query into data history`},
      multiple: `boolean`,
      open: `boolean`,
      'prefix-label': `string`,
      'property-combine-mode': {type: `string`, default: null},
      'project-has-events': {type: `boolean`, default: true},
      'project-has-people': `boolean`,
      'project-has-cohorts': `boolean`,
      'filter-by-types': `json`,
      'project-id': `number`,
      removable: `boolean`,
      'dropdown-alignment': {type: `string`, default: `left`},
      selected: {type: `json`, default: null},
      inverted: `boolean`,
      'selector-icon': `string`,
      'selector-type': {type: `string`, default: SELECTOR_TYPE.TEXT, enum: Object.values(SELECTOR_TYPE)},
      'show-any-event': `boolean`,
      'show-session-event': `boolean`,
      'show-all-events': `boolean`,
      'show-all-tab': `boolean`,
      'show-all-users-cohort': `boolean`,
      'show-top-events': `boolean`,
      'single-data-group': `string`,
      'support-b2b-cohorts': `boolean`,
      'support-inline-cohorts': `boolean`,
      'hide-edit-controls': {
        type: `boolean`,
        description: `show controls that popup modals to edit entities like custom events and properties`,
      },
      'show-special-date-property': `boolean`,
      'show-special-date-property-unit': `boolean`,
      tabs: `json`,
      theme: {type: `string`, default: THEME.PRIMARY, enum: Object.values(THEME)},
      'track-name': {
        type: `string`,
        description: `If specified, includes "Report name" as a property`,
      },
      'other-group-by-options': {type: `string`, default: `[]`},
      'user-id': `number`,
      'workspace-id': `number`,
    };
  }

  get config() {
    return {
      css,
      template,
      useShadowDom: true,
      defaultState: {
        eventData: [],
        isCreatingNewCustomEvent: false,
        isHeaderMenuOpen: false,
        isMenuOpen: false,
        menuType: null,
        permissions: null,
        renderCustomEventModal: false,
        searchFilter: ``,
        selected: null,
        staged: null,
        openModal: null,
        projectID: null,
        tooltipData: null,
        keydownProxyEl: null,
        selectedCustomEventId: null,
      },

      helpers: {
        filterAttributes,
        propagateEvent: (ev) => propagateEvent(this, ev),
        MODAL_TYPE,

        getSelectorTheme: () => {
          let theme = this.attr(`theme`);
          const isDefaultTheme = !this.hasAttribute(`theme`);
          const selectorType = this.attr(`selector-type`);
          const buttonTheme = findKey(ButtonThemes, (value) => value === theme);

          // validate button theme and use THEME.SECONDARY as a default
          if (selectorType === SELECTOR_TYPE.BUTTON) {
            if (!buttonTheme) {
              throw Error(`Unkown button theme`);
            }

            theme = isDefaultTheme ? THEME.SECONDARY : theme;
          }

          return theme;
        },

        getHeaderSegments: () => {
          if (this.attr(`header-text`)) {
            return [
              {
                type: `text`,
                value: this.attr(`header-text`),
              },
            ];
          }
          return this.attr(`header-segments`) || [];
        },

        handleTooltipContextAction: (ev) => {
          if (ev.detail.name === `editCustomEvent`) {
            // TODO handle directly in event menu after moving custom event modal there
            this.helpers.editCustomEvent(ev);
          } else if (ev.detail.name === `editCohort`) {
            // TODO handle directly in properties-menu
            this.helpers.editCohort(ev);
          }
        },

        editCustomEvent: (ev) => {
          this.update({selectedCustomEventId: ev.detail.item.id});
          this.helpers.clickEditCustomEvent(ev);
        },

        editCohort: (ev) => {
          let url = `cohorts#builder/${ev.detail.item.id}`;
          if (ev.detail.item.dataGroupId) {
            url = `${url}/edit/data-group/${ev.detail.item.dataGroupId}`;
          }
          window.open(url, `_blank`);
        },

        getSelectedCustomEventId: () => {
          if (this.state.selectedCustomEventId) {
            return this.state.selectedCustomEventId;
          } else {
            return !this.state.isCreatingNewCustomEvent && this.state.selected ? this.state.selected.id : undefined;
          }
        },

        getHeaderMenuSection: (headerSegment) => [
          {
            // format for mp-items-menu
            items: headerSegment.items.map((item) => ({
              ...item,
              isSelected: item.value === headerSegment.selectedItem.value,
              labelTheme: `strong`,
            })),
          },
        ],

        getTooltipPositionParentElement: () => this.el.querySelector(`mp-drop-menu`),

        getChildMenuName: () => this.childMenuComponentName,
        getTheme: () => this.attr(`theme`),
        isReady: () => !!this.state.projectID || this.state.menuType === `time`,
        labelText: (label) => (label.join ? label.map((l) => l.label).join(` `) : label),
        showMultiPartLabel: () => !this.state.isMenuOpen && Array.isArray(this.helpers.selectedLabel()),
        showInput: () => !this.attr(`input-only`),

        // label config overridden by child menu
        getPrefixLabel: () => this.childMenu.getPrefixLabel(),
        isContentSearchable: () => this.childMenu.isContentSearchable(),
        selectedLabel: () => this.childMenu.selectedLabel(),
        selectedIcon: () => this.childMenu.selectedIcon(),

        labelInputInserted: (vnode) => this.update({keydownProxyEl: vnode.elm}),

        handleHeaderSelectClicked: () => {
          this.update({isHeaderMenuOpen: !this.state.isHeaderMenuOpen});
        },

        handleHeaderMenuVisibilityChange: (ev) => {
          if (ev.detail.state !== `open`) {
            this.update({isHeaderMenuOpen: false});
          }
        },

        handleHeaderSelectionChanged: (ev) => {
          this.update({isHeaderMenuOpen: false});
          this.dispatchEvent(new CustomEvent(`select-header-item`, {detail: ev.detail.item}));
        },

        //#region Custom events
        // disable editing if it is an autotrack event
        isEditableCustomEvent: () => {
          const {eventData} = this.state;
          const isCustomEvent = this.state.selected.custom;

          if (!isCustomEvent) {
            return false;
          }

          const event = eventData && eventData.find((item) => item.value === this.state.selected.value);

          return event && !event.isCollectEverything;
        },

        canWriteCustomEvents: () => {
          const {permissions} = this.state;
          return permissions && permissions.has(`write_customevents`);
        },

        showCreateCustomEvent: () => {
          return (
            this.state.menuType === `events` &&
            this.childMenu.showCreationControls() &&
            !this.attr(`exclude-custom-events`) &&
            this.helpers.canWriteCustomEvents() &&
            !this.attr(`hide-edit-controls`)
          );
        },
        handleCreateCustomEvent: () => {
          this.openCustomEventModal({isCreatingNewCustomEvent: true});
        },
        clickEditCustomEvent: (ev) => {
          ev.stopPropagation(); // do not open the event menu
          this.openCustomEventModal({isCreatingNewCustomEvent: false});
        },
        cancelEditingCustomEventModal: () => {
          this.update({openModal: null, selectedCustomEventId: null});
        },
        closeCustomEventModal: () => {
          this.update({renderCustomEventModal: false, selectedCustomEventId: null});
        },
        deletedCustomEvent: (ev) => {
          const {id} = ev.detail;
          propagateEvent(this, ev);
          const eventData = this.state.eventData.filter((event) => event.id !== id);
          this.update({eventData, openModal: null, selectedCustomEventId: null});
        },
        savedCustomEvent: (ev) => {
          const {customEvent} = ev.detail;
          propagateEvent(this, ev);

          const item = getCustomEventMenuItem(customEvent);
          this.dispatchSelect({item});

          let eventData;
          if (this.state.isCreatingNewCustomEvent) {
            eventData = [...this.state.eventData, item];
          } else {
            const updatedEventIndex = findIndex(this.state.eventData, (event: any) => event.id === customEvent.id);
            eventData = replaceByIndex(this.state.eventData, updatedEventIndex, item);
          }
          this.update({
            eventData,
            openModal: null,
            selectedCustomEventId: null,
          });
        },
        captureTracking: (ev) => {
          const {eventName, props} = ev.detail;
          ev.stopPropagation();
          this.track(eventName, props || {});
        },
        //#endregion

        //#region Custom properties
        // TODO: when we consider permissions for custom properties
        canWriteCustomProperties: () => true,
        handleDeleteCustomProperty: (ev) => this.helpers.handleRemove(ev),
        //#endregion

        preventCloseSelectors: () => {
          // We JSON stringify in order to check if an array exists in an array of arrays.
          const excludeCloseSelectorsJson = JSON.stringify(this.attr(`exclude-close-selectors`));
          return [
            ...(this.attr(`include-custom-properties`) !== `all`
              ? []
              : [[`mp-custom-property-modal`], [`.ace_editor.ace_autocomplete`]]),
            [`mp-context-panel-internal`],
            [`mp-custom-event-modal-internal`],
          ].filter((value) => !excludeCloseSelectorsJson.includes(JSON.stringify(value)));
        },

        handleSearchInput: (ev) => this.update({searchFilter: ev.target.value}),
        handleSelect: (ev) => this.dispatchSelect(ev.detail),
        handleLabelClick: () => this.attr(`disabled`) || this.openMenu(),
        handleMenuChange: (ev) => {
          const {state} = ev.detail;

          if (state === `closed`) {
            this.closeMenu();
          } else if (this.inputElement && this.state.menuType !== `time`) {
            requestAnimationFrame(() => this.inputElement.focus());
          }

          if (state === `open`) {
            this.dispatchEvent(new CustomEvent(`open`));
            scrollIntoViewIfNeeded(ev.target);
          }
        },

        handleRemove: (ev) => {
          ev.stopPropagation();
          this.dispatchEvent(new CustomEvent(`remove`));
        },

        menuInserted: (vnode) => {
          this.childMenu = vnode.elm;
          this.updateSelection();
        },

        searchInputInserted: (vnode) => {
          this.inputElement = vnode.elm;
          this.update({keydownProxyEl: this.inputElement});
        },

        /**
         * Returns all attributes mirrored from this component to the
         * underlying selection menu in a serialized form
         */
        getChildMenuAttributes: () => {
          const [, menuClass] = this.childMenuComponentEntry;
          const attributes = {
            ...pick(this.attrs(), Object.keys(menuClass.attrsSchema)),
            open: this.state.isMenuOpen && this.state.openModal === null,
            'search-filter': this.state.searchFilter,
          };

          return serializeAttributes(attributes);
        },
      },
    };
  }

  constructor() {
    super();

    this.childMenu = Object.create(ChildMenu.prototype, {});
  }

  get childMenuComponentName() {
    const [type] = this.childMenuComponentEntry;
    return `mp-${type}-menu`;
  }

  // attempts to find a multi selection component
  // first and defaults to type-menu
  get childMenuComponentEntry() {
    const multiple = this.attr(`multiple`);
    let type = this.attr(`type`);

    if (multiple && MENU_TYPES[`multi-${type}`]) {
      type = `multi-${type}`;
    }

    return [type, MENU_TYPES[type]];
  }

  openMenu() {
    this.childMenu.handleLabelClick();
    this.update({isMenuOpen: true});
  }

  closeMenu() {
    this.update({
      isMenuOpen: false,
      searchFilter: ``,
      tooltipData: null,
      staged: null,
    });
    this.childMenu.handleCloseMenu();
    this.dispatchEvent(new CustomEvent(`close`));
  }

  openCustomEventModal({isCreatingNewCustomEvent}) {
    this.update({
      isCreatingNewCustomEvent,
      renderCustomEventModal: true,
      openModal: MODAL_TYPE.CustomEvent,
    });
  }

  connectedCallback() {
    super.connectedCallback();
    this.update({projectID: this.attr(`project-id`)});
    this.fetchPermissions();
  }

  attributeChangedCallback(attr, oldVal, newVal) {
    super.attributeChangedCallback(attr, oldVal, newVal);

    switch (attr) {
      case `open`:
        this.update({isMenuOpen: newVal});
        break;
      case `project-id`:
        this.update({projectID: this.attr(`project-id`)});
        break;
      case `selected`:
        this.updateSelection();
        break;
      case `type`:
        if (oldVal !== newVal) {
          this.update({menuType: newVal});
        }
        break;
      default:
        break;
    }
  }

  async fetchPermissions() {
    if (this.state.permissions || !this.attr(`project-id`)) {
      return;
    }

    const userDetails = await userDetailsClient.query({
      projectID: this.attr(`project-id`),
      workspaceId: this.attr(`workspace-id`),
    });
    this.update({permissions: new Set(userDetails.projects[this.attr(`project-id`)].permissions)});
  }

  updateSelection() {
    this.update({selected: this.childMenu.transformSelectedVal(this.attr(`selected`))});
  }

  dispatchSelect(detail) {
    this.dispatchEvent(new CustomEvent(`select`, {detail}));

    if (detail.commit || this.childMenu.shouldCloseOnSelect(detail)) {
      this.closeMenu();
    }
  }

  getSelectedProperty() {
    const {selected} = this.state;
    return selected && selected.length > 0
      ? Object.values(selected[0])[0] // 'segment' format
      : selected; // 'properties' format
  }

  track(event, props, cancelable = true) {
    if (!this.attr(`track-name`)) {
      return;
    }

    emitTrackEvent(this, {
      eventName: event,
      eventPrefix: COMPONENT_NAME,
      props: {
        ...props,
        project_id: this.attr(`project-id`),
        tab: this.attr(`track-name`),
      },
      cancelable,
    });
  }
}

export default registerMPElement(COMPONENT_NAME, QueryBlock);
